/**
 * File Bridge
 * 
 * Handles file selection and access.
 * PARTIAL on web - can use File Input and File System Access API.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities } from '../types'

interface FilePickerOptions {
  accept?: string[]
  multiple?: boolean
}

interface PickedFile {
  name: string
  size: number
  type: string
  lastModified: number
  dataUrl?: string
}

export class FileBridge extends BaseBridge {
  readonly bridgeName = 'File'
  readonly availability: BridgeAvailability = 'partial'
  readonly methods = ['pickFile', 'pickFiles', 'saveFile']
  
  async handleCall(method: string, args: any[]): Promise<any> {
    switch (method) {
      case 'pickFile':
        return JSON.stringify(await this.pickFile(args[0] || {}))
      case 'pickFiles':
        return JSON.stringify(await this.pickFiles(args[0] || {}))
      case 'saveFile':
        return await this.saveFile(args[0], args[1], args[2])
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async pickFile(options: FilePickerOptions): Promise<PickedFile | null> {
    const files = await this.showFilePicker(options, false)
    return files[0] || null
  }
  
  private async pickFiles(options: FilePickerOptions): Promise<PickedFile[]> {
    return this.showFilePicker({ ...options, multiple: true }, true)
  }
  
  private async showFilePicker(options: FilePickerOptions, multiple: boolean): Promise<PickedFile[]> {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.multiple = multiple || options.multiple || false
      
      if (options.accept?.length) {
        input.accept = options.accept.join(',')
      }
      
      input.onchange = async () => {
        const files = Array.from(input.files || [])
        const pickedFiles: PickedFile[] = []
        
        for (const file of files) {
          pickedFiles.push({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          })
        }
        
        resolve(pickedFiles)
      }
      
      input.oncancel = () => {
        resolve([])
      }
      
      input.click()
    })
  }
  
  private async saveFile(
    content: string | Blob, 
    filename: string, 
    mimeType: string = 'application/octet-stream'
  ): Promise<boolean> {
    try {
      // Try File System Access API first (Chrome)
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'File',
              accept: { [mimeType]: [`.${filename.split('.').pop()}`] }
            }]
          })
          
          const writable = await handle.createWritable()
          const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType })
          await writable.write(blob)
          await writable.close()
          
          return true
        } catch (err: any) {
          // User cancelled or API error, fall through to download fallback
          if (err.name === 'AbortError') {
            return false
          }
        }
      }
      
      // Fallback: trigger download
      const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      return true
    } catch {
      return false
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    const hasFileSystemAccess = 'showSaveFilePicker' in window
    
    return {
      pickFile: { available: true },
      pickFiles: { available: true },
      saveFile: { 
        available: true,
        fallback: hasFileSystemAccess ? undefined : 'Uses download fallback'
      }
    }
  }
}

